import { useQuery } from '@tanstack/react-query'
import { OrderStatus } from '@teffy/shared-types'
import { api } from '../services/api'
import { StatCard, Card, Badge, PageHeader } from '../components/ui'

interface DashboardStats {
  topSold: { name: string; qty: number }[]
  topRated: { name: string; avg: number; count: number }[]
}

const STATUS_COLOR: Record<string, 'yellow' | 'blue' | 'purple' | 'green' | 'gray'> = {
  [OrderStatus.REQUESTED]:  'yellow',
  [OrderStatus.CONFIRMED]:  'blue',
  [OrderStatus.PREPARING]:  'purple',
  [OrderStatus.READY]:      'green',
  [OrderStatus.SERVED]:     'gray',
}

const STATUS_LABEL: Record<string, string> = {
  [OrderStatus.REQUESTED]: 'Solicitada',
  [OrderStatus.CONFIRMED]: 'Confirmada',
  [OrderStatus.PREPARING]: 'Preparando',
  [OrderStatus.READY]:     'Lista',
  [OrderStatus.SERVED]:             'Servida',
  [OrderStatus.PAYMENT_REQUESTED]:  'Pago solicitado',
  [OrderStatus.PAID]:               'Pagada',
  [OrderStatus.CANCELLED]:          'Cancelada',
}

interface Order {
  id: string
  status: OrderStatus
  total: number
  createdAt: string
  servedAt: string | null
  table: { number: number }
  items: { id: string }[]
}

export default function DashboardPage() {
  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => (await api.get('/stats/dashboard')).data,
    refetchInterval: 60_000,
  })

  const { data: orders = [], isLoading } = useQuery<Order[]>({
    queryKey: ['dashboard-orders'],
    queryFn: async () => {
      const { data } = await api.get('/orders')
      return data
    },
    refetchInterval: 30_000,
  })

  // Filtrar solo las del día de hoy
  const today = new Date().toDateString()
  const todayOrders = orders.filter(
    (o) => new Date(o.createdAt).toDateString() === today
  )

  const activeOrders = todayOrders.filter(
    (o) => o.status !== OrderStatus.PAID && o.status !== OrderStatus.CANCELLED
  )
  const paidOrders = todayOrders.filter((o) => o.status === OrderStatus.PAID)
  const revenue = paidOrders.reduce((acc, o) => acc + Number(o.total), 0)
  const avgTicket = paidOrders.length ? revenue / paidOrders.length : 0

  // Tiempo promedio de servicio: desde creación hasta servedAt (órdenes servidas hoy)
  const servedToday = todayOrders.filter(
    (o) => o.servedAt && (o.status === OrderStatus.SERVED || o.status === OrderStatus.PAID || o.status === OrderStatus.PAYMENT_REQUESTED)
  )
  const avgServiceMin = (() => {
    if (!servedToday.length) return null
    const totalMs = servedToday.reduce((acc, o) => {
      return acc + (new Date(o.servedAt!).getTime() - new Date(o.createdAt).getTime())
    }, 0)
    return Math.round(totalMs / servedToday.length / 60_000)
  })()

  const serviceTimeLabel = avgServiceMin === null
    ? '—'
    : avgServiceMin < 60
      ? `${avgServiceMin} min`
      : `${Math.floor(avgServiceMin / 60)}h ${avgServiceMin % 60}min`

  return (
    <div className="p-8">
      <PageHeader
        title="Dashboard"
        subtitle={new Date().toLocaleDateString('es-MX', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        })}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard emoji="📋" label="Órdenes activas"      value={activeOrders.length}       sub="en este momento" />
        <StatCard emoji="✅" label="Órdenes pagadas"      value={paidOrders.length}         sub="hoy" />
        <StatCard emoji="💰" label="Ingresos del día"     value={`$${revenue.toFixed(2)}`}  sub="MXN" />
        <StatCard emoji="🎯" label="Ticket promedio"      value={`$${avgTicket.toFixed(2)}`} sub="por mesa" />
        <StatCard
          emoji="⏱"
          label="Tiempo prom. de servicio"
          value={serviceTimeLabel}
          sub={servedToday.length ? `basado en ${servedToday.length} orden(es) de hoy` : 'sin datos hoy aún'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Platillos más vendidos */}
        <Card>
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="font-bold text-gray-900">Platillos más vendidos</h2>
            <p className="text-xs text-gray-400 mt-0.5">Histórico acumulado</p>
          </div>
          {!stats?.topSold.length ? (
            <div className="p-8 text-center text-gray-400 text-sm">Sin datos aún</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {stats.topSold.map((d, i) => (
                <div key={d.name} className="px-5 py-3 flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <span className="flex-1 text-sm font-medium text-gray-800">{d.name}</span>
                  <span className="text-sm font-bold text-gray-900">{d.qty} vendido(s)</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Platillos mejor evaluados */}
        <Card>
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="font-bold text-gray-900">Platillos mejor evaluados</h2>
            <p className="text-xs text-gray-400 mt-0.5">Promedio de estrellas</p>
          </div>
          {!stats?.topRated.length ? (
            <div className="p-8 text-center text-gray-400 text-sm">Sin evaluaciones aún</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {stats.topRated.map((d, i) => (
                <div key={d.name} className="px-5 py-3 flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <span className="flex-1 text-sm font-medium text-gray-800">{d.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-400 text-sm">{'★'.repeat(Math.round(d.avg))}{'☆'.repeat(5 - Math.round(d.avg))}</span>
                    <span className="text-xs text-gray-500">{d.avg.toFixed(1)} ({d.count})</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Órdenes activas */}
        <Card>
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="font-bold text-gray-900">Órdenes en curso</h2>
          </div>
          {isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Cargando…</div>
          ) : activeOrders.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">Sin órdenes activas</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {activeOrders.map((o) => (
                <div key={o.id} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-gray-900">Mesa {o.table.number}</span>
                    <span className="text-xs text-gray-400 ml-2">
                      {new Date(o.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">{o.items.length} platillo(s)</span>
                    <Badge
                      label={STATUS_LABEL[o.status] ?? o.status}
                      color={STATUS_COLOR[o.status] ?? 'gray'}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Últimas pagadas */}
        <Card>
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="font-bold text-gray-900">Últimas mesas pagadas</h2>
          </div>
          {paidOrders.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">Sin pagos hoy aún</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {paidOrders.slice(0, 8).map((o) => (
                <div key={o.id} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-gray-900">Mesa {o.table.number}</span>
                    <span className="text-xs text-gray-400 ml-2">
                      {new Date(o.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <span className="font-bold text-gray-900">${Number(o.total).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
